import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";
import { toastManager } from "../components/DownloadTray/ToastManager";

/**
 * GR-1578: Drag-and-drop reorder for the admin app-preview sidebar.
 *
 * The sortable list contains the full set of visible EventNavigationItem
 * rows. Scheduled-page and implicit-home rows carry [data-undraggable]
 * so they stay in place during reorders; the reorder POST still includes
 * them in DOM order because the server expects every visible row in the
 * payload (see Admin::AppNavigationController#reorder).
 *
 * The Stimulus controller is mounted on a wrapper <div> around the
 * Primer NavList; SortableJS attaches to the inner <ul> that NavList
 * renders.
 *
 * GR-1777: keyboard fallback + ARIA. SortableJS only supports mouse
 * drag, leaving keyboard and screen-reader users without a way to
 * reorder. We expose:
 *   - Space (or Enter) on a focused row to pick it up / drop it.
 *   - ArrowUp/ArrowDown while held to move it past the next draggable row.
 *   - Escape to cancel and restore the original order.
 *   - aria-roledescription on each row so the affordance is discoverable.
 *   - aria-disabled on locked (undraggable) rows.
 *   - A polite live region that announces pickups, moves, drops, cancels.
 */
export class AppNavSortableController extends Controller {
  static values = {
    reorderUrl: String,
    canReorder: { type: Boolean, default: true },
  };

  connect() {
    if (!this.canReorderValue) return;

    this.listElement = this.element.querySelector("ul.ActionListWrap");
    if (!this.listElement) return;

    this.sortable = Sortable.create(this.listElement, {
      animation: 150,
      draggable: "li[data-nav-row]",
      filter: "[data-undraggable]",
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      dragClass: "sortable-drag",
      onEnd: () => this.handleReorder(),
    });

    this.setupAccessibility();
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy();
      this.sortable = null;
    }
    if (this.listElement && this.keydownHandler) {
      this.listElement.removeEventListener("keydown", this.keydownHandler);
    }
    if (this.liveRegion?.parentNode) {
      this.liveRegion.parentNode.removeChild(this.liveRegion);
      this.liveRegion = null;
    }
  }

  // --- Accessibility (GR-1777) --------------------------------------------

  setupAccessibility() {
    this.grabbedRow = null;
    this.originalOrder = null;
    this.liveRegion = this.createLiveRegion();

    const rows = this.listElement.querySelectorAll("li[data-nav-row]");
    rows.forEach((row) => {
      if (row.dataset.undraggable) {
        row.setAttribute("aria-disabled", "true");
        row.setAttribute("aria-roledescription", "Locked navigation item");
      } else {
        row.setAttribute(
          "aria-roledescription",
          "Draggable navigation item — press Space to pick up, then Arrow keys to move"
        );
      }
    });

    this.keydownHandler = this.handleKeydown.bind(this);
    this.listElement.addEventListener("keydown", this.keydownHandler);
  }

  handleKeydown(event) {
    const row = event.target.closest("li[data-nav-row]");
    if (!row || row.dataset.undraggable) return;

    const key = event.key;

    if ((key === " " || key === "Enter") && this.grabbedRow === row) {
      event.preventDefault();
      this.dropRow();
      return;
    }

    if ((key === " " || key === "Enter") && !this.grabbedRow) {
      event.preventDefault();
      this.grabRow(row);
      return;
    }

    if (this.grabbedRow !== row) return;

    if (key === "ArrowDown") {
      event.preventDefault();
      this.move(row, "down");
    } else if (key === "ArrowUp") {
      event.preventDefault();
      this.move(row, "up");
    } else if (key === "Escape") {
      event.preventDefault();
      this.cancelGrab();
    }
  }

  grabRow(row) {
    this.grabbedRow = row;
    this.originalOrder = Array.from(
      this.listElement.querySelectorAll("li[data-nav-row]")
    );
    // aria-grabbed is deprecated in ARIA 1.1 but still announced by major
    // screen readers; pair it with a data attribute the stylesheet can
    // key off of for the visual chosen state.
    row.setAttribute("aria-grabbed", "true");
    row.dataset.grabbed = "true";
    this.announce(
      `Picked up ${this.rowLabel(row)}. Use Arrow Up / Arrow Down to move; ` +
        "Space to drop; Escape to cancel."
    );
  }

  move(row, direction) {
    const allRows = Array.from(
      this.listElement.querySelectorAll("li[data-nav-row]")
    );
    const idx = allRows.indexOf(row);
    const targetIdx = direction === "down" ? idx + 1 : idx - 1;
    if (targetIdx < 0 || targetIdx >= allRows.length) return;

    const sibling = allRows[targetIdx];
    // Locked rows hold their position — skip moves that would cross them.
    if (sibling.dataset.undraggable) return;

    if (direction === "down") {
      row.parentNode.insertBefore(sibling, row);
    } else {
      row.parentNode.insertBefore(row, sibling);
    }

    const newPos =
      Array.from(this.listElement.querySelectorAll("li[data-nav-row]")).indexOf(row) + 1;
    this.announce(
      `${this.rowLabel(row)} moved ${direction} to position ${newPos}.`
    );
  }

  dropRow() {
    const row = this.grabbedRow;
    if (!row) return;
    row.removeAttribute("aria-grabbed");
    delete row.dataset.grabbed;
    const pos =
      Array.from(this.listElement.querySelectorAll("li[data-nav-row]")).indexOf(row) + 1;
    this.announce(`Dropped ${this.rowLabel(row)} at position ${pos}.`);
    this.grabbedRow = null;
    this.originalOrder = null;
    this.handleReorder();
  }

  cancelGrab() {
    const row = this.grabbedRow;
    if (!row) return;
    // Restore the saved order DOM-by-DOM.
    this.originalOrder.forEach((node) => this.listElement.appendChild(node));
    row.removeAttribute("aria-grabbed");
    delete row.dataset.grabbed;
    this.announce(
      `Cancelled. ${this.rowLabel(row)} returned to its original position.`
    );
    this.grabbedRow = null;
    this.originalOrder = null;
  }

  rowLabel(row) {
    return row.textContent.trim().split("\n")[0].trim() || "Navigation item";
  }

  createLiveRegion() {
    const node = document.createElement("div");
    node.setAttribute("aria-live", "polite");
    node.setAttribute("aria-atomic", "true");
    node.setAttribute("data-app-nav-sortable-live-region", "true");
    // Visually hidden but still readable by AT. Inlined so we don't depend
    // on a sr-only class that may not be on the admin shell.
    node.style.cssText =
      "position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0";
    this.element.appendChild(node);
    return node;
  }

  announce(message) {
    if (this.liveRegion) this.liveRegion.textContent = message;
  }

  // --- Reorder POST --------------------------------------------------------

  async handleReorder() {
    const rows = this.listElement.querySelectorAll("li[data-nav-row]");
    const items = Array.from(rows).map((row) => ({
      item_type: row.dataset.itemType,
      item_id: row.dataset.itemId,
    }));

    try {
      await this.postReorder(items);
      toastManager.show({
        message: "Attendee app navigation order updated",
        variant: "success",
        icon: "check",
        autoCloseMs: 2500,
      });
    } catch (err) {
      console.error("App nav reorder failed:", err);
      toastManager.show({
        message: "Couldn't save navigation order",
        subtitle: "Refreshing the preview...",
        variant: "danger",
        icon: "alert",
        autoCloseMs: 3000,
      });
      // Reload after a short delay so the user can read the toast.
      setTimeout(() => window.location.reload(), 800);
    }
  }

  async postReorder(items) {
    const response = await fetch(this.reorderUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
        Accept: "application/json",
      },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.content : "";
  }
}
